import type { ReactNode } from 'react';
import { ConnectPasswordFields, type ConnectPasswordControl } from '../ConnectPassword';
import { SELF_CONNECT_BLOCKED, SELF_DEVICE_BADGE } from '../myConnection/useMyDeviceCode';
import { RenameButton, RenameEditor, type RenameState } from './RenameField';
import { ENTRY_ALIAS_MAX } from './InlineForms';

/** ID 항목 아이콘·표시 — 관리 기기(🖥)와 구별(사용자 결정 2026-10-06). */
export const ENTRY_ICON = '🔖';
export const ENTRY_BADGE = 'ID';
export const ENTRY_TITLE = 'ID로 추가한 기기 — 코드만 저장(접속할 때마다 비밀번호 입력)';

/**
 * ID 항목 한 줄(설계서 §14.20) — 조직 공유 항목과 "내 주소록" 이 함께 쓴다.
 * 접속은 **코드**로(기존 코드 접속 경로 — 비밀번호를 받는다).
 *
 * @param status 상태 한 줄(온라인·관리 기기로 등록됨 등 — 출처마다 다르다)
 * @param actions 관리 단추(삭제·그룹 이동 등 — 권한·출처마다 다르다)
 * @param picker 그룹 이동 중이면 고르기 칸(단추 자리를 대신한다)
 * @param rename 이름 변경(바꿀 권한이 있을 때만 — 조직 공유는 관리자, 내 주소록은 본인). 없으면 단추도 없다.
 * @param rowBusy 이 행 작업 중(이름 저장 등)
 */
export function EntryRow({
  alias,
  deviceCode,
  online,
  status,
  busy,
  isSelf,
  pw,
  actions,
  picker,
  dataId,
  rename,
  rowBusy = false,
}: {
  rename?: RenameState;
  rowBusy?: boolean;
  alias: string;
  deviceCode: string;
  /** null = 모름(조직 밖 기기). */
  online: boolean | null;
  status?: string;
  busy: boolean;
  isSelf: boolean;
  pw: ConnectPasswordControl;
  actions?: ReactNode;
  picker?: ReactNode | null;
  dataId?: string;
}) {
  return (
    <div
      className="list-item gtree-row"
      title={isSelf ? SELF_CONNECT_BLOCKED : '더블클릭하여 접속(비밀번호 입력)'}
      // 이름 편집 중에는 더블클릭 접속을 하지 않는다(편집 칸 안 더블클릭이 접속으로 새지 않게).
      onDoubleClick={() => !busy && !isSelf && !rename?.active && pw.ask(deviceCode, alias)}
      data-entry-id={dataId}
    >
      <span className={`dot ${online === true ? 'ok' : online === false ? 'err' : ''}`} />
      <span className="gtree-icon" title={ENTRY_TITLE} aria-label="ID로 추가한 기기">
        {ENTRY_ICON}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {rename?.active ? (
          <RenameEditor rename={rename} busy={rowBusy} maxLength={ENTRY_ALIAS_MAX} />
        ) : (
          <div className="name">
            {alias}
            <span className="badge gtree-id-badge" title={ENTRY_TITLE}>
              {ENTRY_BADGE}
            </span>
            {isSelf && (
              <span className="badge brand" title={SELF_CONNECT_BLOCKED} style={{ marginLeft: 8 }}>
                {SELF_DEVICE_BADGE}
              </span>
            )}
          </div>
        )}
        <div className="sub">
          {deviceCode}
          {status ? ` · ${status}` : ''}
        </div>
      </div>
      {picker ??
        (pw.target === deviceCode ? (
          <ConnectPasswordFields ctl={pw} busy={busy} />
        ) : (
          <>
            {rename && <RenameButton rename={rename} busy={rowBusy} title="표시 이름을 바꿉니다(ID·원격 기기에는 영향 없음)." />}
            {actions}
            <button onClick={() => pw.ask(deviceCode, alias)} disabled={busy || isSelf} title={isSelf ? SELF_CONNECT_BLOCKED : undefined}>
              접속
            </button>
          </>
        ))}
    </div>
  );
}
